import { useMutation } from '@apollo/client';
import { renderChildren, Children } from '@sixfold/typed-render-props';
import { gql } from 'graphql-tag';
import React from 'react';

import {
  AddUserToCompanyMutation as AddUserToCompanyMutationResult,
  AddUserToCompanyMutationVariables,
  RemoveUserFromCompanyMutation as RemoveUserFromCompanyMutationResult,
  RemoveUserFromCompanyMutationVariables,
  ChangeUserRoleInCompanyMutation as ChangeUserRoleInCompanyMutationResult,
  ChangeUserRoleInCompanyMutationVariables,
  ChangeUserMetadataInCompanyMutation as ChangeUserMetadataInCompanyMutationResult,
  ChangeUserMetadataInCompanyMutationVariables,
} from '../../lib/graphql/result_types';
import { userInCompanyDetailsFragment } from '../graphql';

const addUserToCompanyMutation = gql`
  ${userInCompanyDetailsFragment}

  mutation AddUserToCompany($input: AddUserToCompanyInput!) {
    addUserToCompany(input: $input) {
      ...UserInCompanyDetails
    }
  }
`;

const removeUserFromCompany = gql`
  mutation RemoveUserFromCompany($input: RemoveUserFromCompanyInput!) {
    removeUserFromCompany(input: $input)
  }
`;

const changeUserRoleInCompany = gql`
  ${userInCompanyDetailsFragment}

  mutation ChangeUserRoleInCompany($input: ChangeUserRoleInCompanyInput!) {
    changeUserRoleInCompany(input: $input) {
      ...UserInCompanyDetails
    }
  }
`;

const changeUserMetadataInCompany = gql`
  ${userInCompanyDetailsFragment}

  mutation ChangeUserMetadataInCompany($input: ChangeUserMetadataInCompanyInput!) {
    changeUserMetadataInCompany(input: $input) {
      ...UserInCompanyDetails
    }
  }
`;

interface Props {
  children: Children<{
    addUserToCompany: (
      input: AddUserToCompanyMutationVariables['input'],
    ) => Promise<{ userId: string; companyId: string } | null>;
    removeUserFromCompany: (input: RemoveUserFromCompanyMutationVariables['input']) => Promise<void>;
    changeUserRoleInCompany: (input: ChangeUserRoleInCompanyMutationVariables['input']) => Promise<void>;
    changeUserMetadataInCompany: (input: ChangeUserMetadataInCompanyMutationVariables['input']) => Promise<void>;
  }>;
}

export const UserInCompanyMutations: React.FC<Props> = (props) => {
  const [addUserToCompanyFn] = useMutation<AddUserToCompanyMutationResult, AddUserToCompanyMutationVariables>(
    addUserToCompanyMutation,
  );
  const [removeUserFromCompanyFn] = useMutation<
    RemoveUserFromCompanyMutationResult,
    RemoveUserFromCompanyMutationVariables
  >(removeUserFromCompany);
  const [changeUserRoleInCompanyFn] = useMutation<
    ChangeUserRoleInCompanyMutationResult,
    ChangeUserRoleInCompanyMutationVariables
  >(changeUserRoleInCompany);
  const [changeUserMetadataInCompanyFn] = useMutation<
    ChangeUserMetadataInCompanyMutationResult,
    ChangeUserMetadataInCompanyMutationVariables
  >(changeUserMetadataInCompany);

  return renderChildren(props.children as React.ReactNode, {
    addUserToCompany: async (input: AddUserToCompanyMutationVariables['input']) => {
      const result = await addUserToCompanyFn({
        variables: { input },
      });

      const addUserToCompanyUserId = result !== undefined ? result.data?.addUserToCompany?.user.user_id : undefined;

      if (addUserToCompanyUserId === undefined) {
        return null;
      }

      return { userId: addUserToCompanyUserId, companyId: input.companyId };
    },
    removeUserFromCompany: (input: RemoveUserFromCompanyMutationVariables['input']) => {
      return removeUserFromCompanyFn({
        variables: { input },
      });
    },
    changeUserRoleInCompany: (input: ChangeUserRoleInCompanyMutationVariables['input']) => {
      return changeUserRoleInCompanyFn({
        variables: { input },
      });
    },
    changeUserMetadataInCompany: (input: ChangeUserMetadataInCompanyMutationVariables['input']) => {
      return changeUserMetadataInCompanyFn({
        variables: { input },
      });
    },
  });
};
